// Week 10 / Day 1 — Money Light: Budget and Choice (bilingual RU/EN inline).
// Quest-style, playful review of Week 1 ideas. Universal ages 7–15; Max uses the middle level.
// Educational only — fictional nims, no real money decisions.

export const maxWeek10Day1 = {
  id: 'max-week-10-day-1',
  studentId: 'max',
  weekId: 'week-10',
  dayId: 'day-1',
  title: 'Огонёк денег: бюджет и выбор / Money Light: Budget and Choice',
  shortTitle: 'Огонёк денег / Money Light',
  theme:
    'Нимни вспоминает: деньги помогают выбирать, считать и планировать. / Nimny remembers: money helps us choose, calculate and plan.',
  estimatedTimeLabel:
    'Квестовая часть: около 20–30 минут. Паспорт миссии и короткая проверка с родителем: около 35–50 минут. / Quest part: about 20–30 minutes. Mission passport and short parent check: about 35–50 minutes.',
  nimnyIntro:
    'Нимни нашла первый огонёк знаний. Чтобы он зажёгся, нужно вспомнить деньги, цену, доход, расход, бюджет и выбор. Это не экзамен — это лёгкая миссия. / Nimny found the first light of knowledge. To light it up, remember money, price, income, expense, budget and choice. This is not an exam — it is a light mission.',
  goals: [
    'Огонёк знаний № 1: деньги, бюджет и выбор. / Light of knowledge #1: money, budget and choice.',
    'Слой 1. Математика / Layer 1. Math: лёгкие задачки с нимами (Grade 5 / middle).',
    'Слой 2. Русский язык / Layer 2. Russian: словарь недели № 1 и мини-диктант.',
    'Слой 3. Чтение и короткое письмо / Layer 3. Reading and short writing: 3–5 предложений «Как деньги помогают выбирать?». / "How does money help us choose?".',
    'Слой 4. Карточки памяти / Layer 4. Memory cards: доход / расход / нужно / хочу / отложить.',
    'Слой 5. Паспорт миссии / Layer 5. Mission passport: выбор бюджета и рисунок огонька. / Budget choice and draw the light.',
    'Дневник мышления / Thinking Diary.',
    'Награда нимами / Reward in nims: 3/5.'
  ],
  vocabulary: [
    { term: 'деньги / money', definition: 'способ обмена, который мы считаем / a way of exchange we count' },
    { term: 'цена / price', definition: 'сколько стоит / how much it costs' },
    { term: 'доход / income', definition: 'нимы пришли / nims came in' },
    { term: 'расход / expense', definition: 'нимы ушли / nims went out' },
    { term: 'бюджет / budget', definition: 'план, как использовать нимы / a plan how to use nims' },
    { term: 'выбор / choice', definition: 'решение, что взять / a decision what to take' },
    { term: 'нужно / need', definition: 'без этого трудно / hard to live without' },
    { term: 'хочу / want', definition: 'было бы приятно / would be nice' },
    { term: 'отложить / to save', definition: 'оставить на цель / keep for a goal' },
    { term: 'остаток / balance', definition: 'сколько нимов осталось / how many nims are left' }
  ],
  onlineTasks: [
    { id: 'q1', type: 'math', title: 'Сколько нимов осталось? / How many nims are left?',
      prompt: 'У Нимни 50 нимов. Книга — 18 нимов, подарок — 12 нимов. Сколько осталось? / Nimny has 50 nims. Book — 18, gift — 12. How many are left?',
      helper: '50 − 18 − 12. / 50 − 18 − 12.', answer: '20', unit: 'нимов / nims' },
    { id: 'q2', type: 'math', title: 'Что происходит с балансом? / What happens to the balance?',
      prompt: 'Карточка дохода: +20 нимов. Карточка расхода: −8 нимов. Какое общее изменение? / Income card: +20. Expense card: −8. Total change?',
      helper: '+20 − 8. / +20 − 8.', answer: '+12', unit: 'нимов / nims' },
    { id: 'q3', type: 'math', title: 'Копилка за 4 дня / Save jar over 4 days',
      prompt: 'Нимни откладывает 5 нимов каждый день 4 дня. Сколько отложила? / Nimny saves 5 nims a day for 4 days. How many in total?',
      helper: '5 × 4. / 5 × 4.', answer: '20', unit: 'нимов / nims' },
    { id: 'q4', type: 'classify', title: 'Карточки памяти / Memory cards',
      prompt: 'Распредели карточки по 5 домикам. / Sort the cards into 5 houses.',
      categories: [
        { id: 'income', label: 'Доход / Income' },
        { id: 'expense', label: 'Расход / Expense' },
        { id: 'need', label: 'Нужно / Need' },
        { id: 'want', label: 'Хочу / Want' },
        { id: 'save', label: 'Отложить / Save' }
      ],
      items: [
        { id: 'got20', text: 'получил 20 нимов / received 20 nims' },
        { id: 'book', text: 'купил книгу / bought a book' },
        { id: 'water', text: 'вода / water' },
        { id: 'toy', text: 'новая игрушка / new toy' },
        { id: 'goal5', text: 'оставил 5 нимов на цель / kept 5 nims for a goal' },
        { id: 'gift', text: 'подарок другу / gift to a friend' },
        { id: 'notebook', text: 'тетрадь для учёбы / notebook for study' }
      ] },
    { id: 'q5', type: 'text', title: 'Исправь ошибки / Fix the mistakes',
      prompt: 'Перепиши без ошибок и расставь знаки: «бюджет памагает выбирать сначала падумай что нужна а потом что хочеца». / Rewrite without mistakes (Russian sentence above).',
      placeholder: 'Бюджет помогает выбирать. / A budget helps to choose.' },
    { id: 'q6', type: 'classify', title: 'Выбор бюджета / Budget choice',
      prompt: 'Бюджет — 50 нимов. Выбери набор, который подходит (сумма ≤ 50): еда 15, книга 20, игрушка 25, отложить 10. / Budget = 50 nims. Choose a valid set (sum ≤ 50). Cards: food 15, book 20, toy 25, save 10.',
      categories: [
        { id: 'fits', label: 'Подходит (≤ 50) / Fits' },
        { id: 'too_much', label: 'Слишком много (> 50) / Too much' }
      ],
      items: [
        { id: 'fbs', text: 'еда 15 + книга 20 + отложить 10 = 45 / food + book + save' },
        { id: 'fts', text: 'еда 15 + игрушка 25 + отложить 10 = 50 / food + toy + save' },
        { id: 'btsx', text: 'книга 20 + игрушка 25 + отложить 10 = 55 / book + toy + save' },
        { id: 'all', text: 'еда 15 + книга 20 + игрушка 25 + отложить 10 = 70 / all four' },
        { id: 'fb', text: 'еда 15 + книга 20 = 35 / food + book' }
      ] },
    { id: 'q7', type: 'essay', title: 'Как деньги помогают выбирать? / How does money help us choose?',
      prompt: 'Напиши 3–5 предложений по-русски: как деньги помогают выбирать? Используй слова: бюджет, нужно, хочу, отложить, выбор. / Write 3–5 Russian sentences using the keywords.',
      minSentences: 3, maxSentences: 5, placeholder: 'Деньги помогают мне выбирать, потому что... / Money helps me choose because...' },
    { id: 'q8', type: 'essay', title: 'Тихий вопрос Нимни / A quiet question from Nimny',
      prompt: 'Напиши 2–4 предложения: что было самым важным в этой миссии? Используй слово «огонёк». / Write 2–4 sentences about the most important thing in this mission; use the word "огонёк".',
      minSentences: 2, maxSentences: 4, placeholder: 'Самое важное в первом огоньке знаний — это... / The most important in the first light of knowledge is...' }
  ],
  notebookInstructions: [
    'Заголовок: «Неделя 10, День 1 — Огонёк денег». / Heading: "Week 10, Day 1 — Money Light".',
    'Слой 1. Математика / Layer 1. Math: q1–q3 + опц. 1/10 от 50 = 5. / + optional one tenth of 50.',
    'Слой 2. Русский язык / Layer 2. Russian: словарь и мини-диктант: «Бюджет помогает выбирать. Сначала подумай, что нужно, а потом что хочется.» + правка. / vocabulary, mini-dictation, edit.',
    'Слой 3. Чтение и короткое письмо / Layer 3. Reading and short writing: 3–5 предложений «Как деньги помогают выбирать?». / 3–5 sentences.',
    'Слой 4. Карточки памяти / Layer 4. Memory cards: 7 карточек по 5 домикам. / 7 cards into 5 houses.',
    'Слой 5. Паспорт миссии / Layer 5. Mission passport: выбор бюджета и рисунок первого огонька. / Budget choice and the first light drawing.',
    'Дневник мышления / Thinking diary.',
    'Награда нимами после проверки. / Reward in nims after parent check.'
  ],
  printWorksheet: {
    title: 'Неделя 10, День 1 / Week 10, Day 1',
    subtitle: 'Огонёк денег: бюджет и выбор / Money Light: Budget and Choice',
    pages: [
      { title: 'Маршрут миссии / Mission route', blocks: [
        { type: 'text', text: 'Поставь штамп ✦, когда выполнено. / Stamp ✦ when done.' },
        { type: 'list', items: [
          '✦ 1. Быстрая математика / Quick math.',
          '✦ 2. Русский язык — слова и мини-диктант / Russian — words and mini-dictation.',
          '✦ 3. Короткое письмо «Как деньги помогают выбирать?» / Short writing.',
          '✦ 4. Карточки памяти / Memory cards.',
          '✦ 5. Выбор бюджета и рисунок огонька / Budget choice and light drawing.',
          '✦ 6. Дневник мышления / Thinking diary.',
          '✦ 7. Проверка с родителем / Parent check.',
          '✦ 8. Печать миссии и нимы / Mission stamp and nims.'
        ] }
      ] },
      { title: 'Слой 1. Математика — быстрая миссия / Layer 1. Math — Quick mission', blocks: [
        { type: 'list', items: [
          '1) 50 − 18 − 12 = ____ нимов осталось / nims left.',
          '2) +20 − 8 = ____ нимов общее изменение / total change.',
          '3) 5 × 4 = ____ нимов отложено за 4 дня / saved in 4 days.',
          '4) Какие наборы (≤ 50 нимов) подходят? / Which sets fit?: ▢ еда 15 + книга 20 + отложить 10 ▢ еда 15 + игрушка 25 + отложить 10 ▢ книга 20 + игрушка 25 + отложить 10 ▢ все четыре / all four ▢ еда 15 + книга 20.',
          '5) Optional / Доп.: 1/10 от 50 = ____ нимов / one tenth of 50.',
          '6) Optional / Доп.: 50 − 45 = ____ нимов остатка / balance after fits set.'
        ] }
      ] },
      { title: 'Слой 2. Русский язык — слова и мини-диктант / Layer 2. Russian — Words and Mini-Dictation', blocks: [
        { type: 'text', text: 'Лёгкое повторение: словарь огонька денег. / Light review: money-light vocabulary.' },
        { type: 'list', items: [
          'Словарь / Vocabulary: деньги, цена, доход, расход, бюджет, выбор, нужно, хочу, отложить, остаток.',
          'Мини-диктант / Mini-dictation: «Бюджет помогает выбирать. Сначала подумай, что нужно, а потом что хочется.»',
          'Исправь ошибки / Fix mistakes: «бюджет памагает выбирать сначала падумай что нужна а потом что хочеца».',
          'Орфограмма дня / Spelling rule of the day: безударные гласные и пунктуация. / Unstressed vowels and punctuation.'
        ] }
      ] },
      { title: 'Слой 3. Чтение и короткое письмо / Layer 3. Reading and Short Writing', blocks: [
        { type: 'list', items: [
          'Прочитай ещё раз слова огонька денег. / Re-read the money-light vocabulary.',
          'Напиши 3–5 предложений «Как деньги помогают выбирать?» / Write 3–5 Russian sentences "How does money help us choose?": ____________________________________________',
          'Используй слова: бюджет, нужно, хочу, отложить, выбор / Use the keywords.'
        ] }
      ] },
      { title: 'Слой 4. Карточки памяти и сортировка / Layer 4. Memory Cards and Sorting', blocks: [
        { type: 'text', text: 'Вырежи или отметь карточки и положи в 5 домиков. / Cut or mark cards and place into 5 houses.' },
        { type: 'list', items: [
          '«получил 20 нимов» → ▢ Доход ▢ Расход ▢ Нужно ▢ Хочу ▢ Отложить',
          '«купил книгу» → ▢ Доход ▢ Расход ▢ Нужно ▢ Хочу ▢ Отложить',
          '«вода» → ▢ Доход ▢ Расход ▢ Нужно ▢ Хочу ▢ Отложить',
          '«новая игрушка» → ▢ Доход ▢ Расход ▢ Нужно ▢ Хочу ▢ Отложить',
          '«оставил 5 нимов на цель» → ▢ Доход ▢ Расход ▢ Нужно ▢ Хочу ▢ Отложить',
          '«подарок другу» → ▢ Доход ▢ Расход ▢ Нужно ▢ Хочу ▢ Отложить',
          '«тетрадь для учёбы» → ▢ Доход ▢ Расход ▢ Нужно ▢ Хочу ▢ Отложить'
        ] }
      ] },
      { title: 'Слой 5. Огонёк знаний — рисунок и практика / Layer 5. Knowledge Light — Drawing and Practical', blocks: [
        { type: 'text', text: 'Нарисуй первый огонёк знаний — Огонёк денег. Подпиши «Я умею считать бюджет». / Draw the first light. Sign "Я умею считать бюджет".' },
        { type: 'list', items: [
          'Рисунок огонька денег / Money-light drawing: ▢▢▢',
          'Подпись / Caption: «Я умею считать бюджет.»',
          'Мой бюджет на 50 нимов / My 50-nims budget: еда ____, книга ____, игрушка ____, отложить ____.',
          'Почему я выбрал именно так / Why I chose this way: ____________________________________________'
        ] }
      ] },
      { title: 'Дневник мышления / Thinking Diary', blocks: [
        { type: 'list', items: [
          'Что было самым весёлым в первой миссии? / Most fun in mission #1: ___________________',
          'Что было самым важным? / Most important: ___________________'
        ] }
      ] },
      { title: 'Родительская проверка, печать миссии и награда нимами / Parent check, mission stamp and reward in nims', blocks: [
        { type: 'list', items: [
          'Печать миссии / Mission stamp: ▢ ✦ ОГОНЁК ДЕНЕГ ЗАЖЖЁН ✦ / MONEY LIGHT IS LIT.',
          'Базовая награда / Base reward: 3 нима / nims ✓',
          'Бонус за разумный выбор бюджета / Bonus for a sensible budget choice: +1 ☐',
          'Бонус за ясное объяснение по-русски / Bonus for a clear Russian explanation: +1 ☐',
          'Итого / Total: ____ из / of 5 нимов / nims'
        ] }
      ] }
    ]
  },
  parentGuide: {
    goal:
      'Лёгкая квестовая миссия: повторить идеи Недели 1 (деньги, доход, расход, бюджет, выбор, отложить). Самостоятельная работа; роль родителя — короткое введение (3–5 минут), помощь только если ребёнок застрял, и финальная проверка (5–10 минут). Не экзамен. / Light quest mission reviewing Week 1. Child-led; parent gives 3–5 min intro, helps only if stuck, 5–10 min final check. Not a test.',
    safetyNote: 'Только учебная симуляция в нимах; никаких реальных решений с настоящими деньгами. / Educational simulation in nims only; no real money decisions.',
    mathGoal: 'q1: 50−18−12 = 20. q2: +20−8 = +12. q3: 5×4 = 20. q6: подходят — еда 15 + книга 20 + отложить 10 (45), еда 15 + игрушка 25 + отложить 10 (50), еда 15 + книга 20 (35); слишком много — книга 20 + игрушка 25 + отложить 10 (55), все четыре (70). Опц.: 1/10 от 50 = 5; 50−45 = 5. / Listed.',
    russianGoal: 'Словарь огонька денег запомнен; мини-диктант чистый; правка убирает все ошибки и ставит знаки. / Vocabulary recalled; clean dictation; mistakes fixed.',
    readingWritingGoal: '3–5 предложений по-русски с минимум 3 ключевыми словами; пересказ 1–2 предложениями родителю по желанию. / 3–5 Russian sentences using ≥ 3 keywords; optional 1–2 sentence retelling.',
    missionGoal: '7 карточек памяти распределены по 5 домикам без явных ошибок. / 7 memory cards placed into 5 houses without clear errors.',
    practicalGoal: 'Один разумный бюджет на 50 нимов выбран и обоснован: сумма не превышает 50, есть нужно/хочу/отложить, рисунок огонька подписан «Я умею считать бюджет». / One sensible 50-nim budget chosen with justification; light drawing signed.',
    check: [
      'Q1: 20 нимов / 20 nims.',
      'Q2: +12 нимов / +12 nims.',
      'Q3: 20 нимов / 20 nims.',
      '«доход / расход / нужно / хочу / отложить» — карточки разложены / Cards sorted.',
      'Бюджет на 50 нимов выбран и обоснован / 50-nim budget chosen and justified.',
      '3–5 предложений «Как деньги помогают выбирать?» / 3–5 sentences.',
      'Дневник мышления заполнен / Thinking diary filled.'
    ],
    answerKey: [
      { taskId: 'q1', answer: '50 − 18 − 12 = 20 нимов. / 20 nims.' },
      { taskId: 'q2', answer: '+20 − 8 = +12 нимов (баланс растёт на 12). / +12 nims.' },
      { taskId: 'q3', answer: '5 × 4 = 20 нимов отложено. / 20 nims saved.' },
      { taskId: 'q4', answer: 'Доход: получил 20 нимов. Расход: купил книгу, новая игрушка, подарок другу, тетрадь для учёбы. Нужно: вода, тетрадь для учёбы. Хочу: новая игрушка, подарок другу. Отложить: оставил 5 нимов на цель. (Карточка может попасть в 1–2 категории — принимаем разумные варианты.) / Listed; some cards fit 2 categories.' },
      { taskId: 'q5', answer: 'Бюджет помогает выбирать. Сначала подумай, что нужно, а потом что хочется.' },
      { taskId: 'q6', answer: 'Подходит: еда 15 + книга 20 + отложить 10 (45); еда 15 + игрушка 25 + отложить 10 (50); еда 15 + книга 20 (35). Слишком много: книга 20 + игрушка 25 + отложить 10 (55); все четыре (70). / Listed.' },
      { taskId: 'q7', answer: 'Открытый ответ. 3–5 предложений по-русски с минимум 3 ключевыми словами и идеей: бюджет помогает сначала выбрать нужное, потом немного хочется, и кое-что отложить. / Open answer.' },
      { taskId: 'q8', answer: 'Открытый ответ. 2–4 предложения; слово «огонёк» использовано. / Open answer.' }
    ],
    spellingCheck: 'Словарь и диктант чистые; правка корректна. / Vocabulary recalled; clean dictation; edit correct.',
    readingCheck: '3–5 предложений по-русски; ключевые слова использованы. / 3–5 Russian sentences with keywords.',
    missionCheck: '7 карточек памяти разложены без явных ошибок; бюджет на 50 нимов выбран и обоснован. / 7 cards sorted; 50-nim budget chosen and justified.',
    notebookCheck: 'Дневник мышления заполнен; рисунок огонька подписан. / Diary filled; light drawing signed.',
    printCheck: 'Все 8 страниц паспорта миссии заполнены. / All 8 mission-passport pages completed.',
    discussionPrompts: [
      'Что такое доход? Что такое расход? / What is income? What is expense?',
      'Зачем нужен бюджет? / Why is a budget useful?',
      'Что ты выбрал и почему? / What did you choose and why?',
      'Что значит «отложить»? / What does "to save" mean?'
    ],
    parentRoleNote: 'Введение 3–5 минут, помощь только если застрял, финальная проверка 5–10 минут и начисление нимов. Не экзамен. / 3–5 min intro, help only if stuck, 5–10 min final check, then award nims. Not a test.',
    ifEasy: 'Усложните: попросите придумать второй бюджет на 100 нимов и обосновать его. / Make harder: design a 100-nim budget and justify.',
    ifDifficult: 'Сократите: оставьте q1, q4 и рисунок огонька. / Make easier: keep q1, q4 and the light drawing.',
    tokenReward:
      'После проверки можно начислить бонусные нимы через Parent Room. Нимы — это призовые токены курса, а не оплата. / After checking the work, the parent may add bonus nims through Parent Room. Nims are course prize tokens, not payment.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за миссию / Mission reward', display: '3 нима / 3 nims',
    bonuses: [
      { id: 'budget-choice', label: 'Бонус за разумный выбор бюджета / Bonus for a sensible budget choice', amount: 1 },
      { id: 'clear-russian-answer', label: 'Бонус за ясное объяснение по-русски / Bonus for a clear Russian explanation', amount: 1 }
    ]
  },
  completionRequirements: { requiredTaskIds: ['q1', 'q4', 'q6', 'q7', 'q8'], allowNotebookCompletion: true },
  unlockAfter: { weekId: 'week-9', dayId: 'day-5' },
  nextLessonId: 'max-week-10-day-2'
};

export default maxWeek10Day1;
